import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { logError } from '@zg-rentals/log-error';

import MicroDepositsPage from './component';
import { ErrorInterface } from '@common/types';
import { getRachelDashboard, requestUpdateModeLinkToken } from '@renter-hub/actions/requests';
import { editPaymentInfo } from '@renter-hub/actions/onboarding';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import {
  DEFAULT_ERROR,
  PAYMENT_METHOD_UNPROCESSABLE,
  PAYMENT_UPDATED_SUCCESS,
} from '@renter-hub/constants/notifications';
import HTTP_STATUS from '@common/constants/HttpConstants';
import paths from '@renter-hub/pathsManifest';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

const VERIFIED_STATUSES = new Set([VERIFICATION_STATUS.VERIFIED, VERIFICATION_STATUS.UNKNOWN]);

const mapStateToProps = (state) => {
  const accountsNeedingVerification = state.payments.renter.dashboard.paymentMethods.filter(
    (method) => method.paymentMethodStatus && !VERIFIED_STATUSES.has(method.paymentMethodStatus),
  );

  const mostRecentAccountNeedingVerification =
    accountsNeedingVerification.length > 0 &&
    [...accountsNeedingVerification].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];

  return {
    mostRecentAccountNeedingVerification,
    config: state.payments.renter.config,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId, brand, location }) => {
  const handleError = async (error: unknown) => {
    const err = error as ErrorInterface;
    let notificationType = DEFAULT_ERROR;
    let message: string | undefined;

    if (err instanceof Error) {
      message = err.message;
    }

    if (err.action && err.action.payload) {
      const { status } = err.action.payload;

      // If 401 status, redirect to sign-in
      if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location = getSignInPage();
        return null;
      }

      // If 422 status, show payment method unprocessable banner
      if (status === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
        notificationType = PAYMENT_METHOD_UNPROCESSABLE;
      }
    }

    logError({ error });
    dispatch(showPageNotification(notificationType, { brand, message }));
    return { error: true };
  };

  return {
    routeToDashboard: () => dispatch(push(baseUrl)),
    onPaymentMethodCreationSuccess: async () => {
      try {
        // Request the latest renter status
        const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
        await checkRequestActionSuccess(renterStatusResponse);

        const { moveInCostsDetails, nextPayable } = renterStatusResponse.payload.renterStatus || {};
        const fromDashboard = location?.state?.fromDashboard;
        let toUrl;

        if (nextPayable.isScheduled || fromDashboard) {
          toUrl = baseUrl;
        } else if (moveInCostsDetails?.items?.length > 0 && moveInCostsDetails?.isScheduled === false) {
          toUrl = baseUrl + paths.moveInCostsSchedulingPage;
        } else {
          toUrl = baseUrl + paths.paymentSettingsPage;
        }

        await dispatch(push(toUrl));
        // Show the payment updated success banner
        await dispatch(showPageNotification(PAYMENT_UPDATED_SUCCESS));

        return { error: false };
      } catch (err) {
        return handleError(err);
      }
    },
    onPaymentMethodCreationError: handleError,
    getPlaidUpdateModeLinkToken: (externalId) =>
      Promise.resolve()
        .then(() => dispatch(closeNotification()))
        .then(() => dispatch(requestUpdateModeLinkToken(externalId)))
        .catch((err) => {
          logError({ error: err });
          return dispatch(showPageNotification(DEFAULT_ERROR, { brand }));
        }),
    onEditPaymentInfo: async () => {
      await dispatch(closeNotification());
      await dispatch(editPaymentInfo());
      await dispatch(push(baseUrl + paths.editPaymentInfoPage, { fromWhere: paths.plaidConnectPage }));
    },
    trackValidationError: (data) => dispatch(trackValidationError(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MicroDepositsPage);
